import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { useAdminAuth } from "@/context/AdminAuthContext";
import AdminShell from "@/components/admin/AdminShell";
import { SearchInput, PaginationBar } from "@/components/admin/ListControls";
import { useDebounced } from "@/hooks/useDebounced";
import { usePagedList } from "@/hooks/usePagedList";
import { QUOTE_STATUS_LABELS, euros, formatDate } from "@/constants/business_status";

export default function QuotesPage() {
  const { admin } = useAdminAuth();
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");
  const q = useDebounced(searchInput, 350);
  const [status, setStatus] = useState("all");
  const params = {};
  if (q.trim()) params.q = q.trim();
  if (status !== "all") params.status = status;
  const list = usePagedList("/admin/quotes", params, [q, status]);

  if (admin === null) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-navy" /></div>;
  if (admin === false) return <Navigate to="/admin/login" replace />;

  return (
    <AdminShell>
      <div className="flex flex-wrap gap-3 mb-5 items-center">
        <h1 className="font-heading font-extrabold text-2xl text-navy mr-auto">Devis</h1>
        <SearchInput value={searchInput} onChange={setSearchInput} testId="quotes-search-input" placeholder="Rechercher numéro, client…" />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="quotes-filter-status" className="w-52 rounded-xl bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            {Object.entries(QUOTE_STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="bg-white rounded-2xl shadow-sm overflow-x-auto">
        <Table data-testid="quotes-table">
          <TableHeader><TableRow>
            <TableHead>Numéro</TableHead><TableHead>Client</TableHead><TableHead>Titre</TableHead>
            <TableHead>Version</TableHead><TableHead>Total TTC</TableHead><TableHead>Validité</TableHead>
            <TableHead>Statut</TableHead><TableHead>Mis à jour</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {list.loading ? (
              <TableRow><TableCell colSpan={9} className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin mx-auto text-navy" /></TableCell></TableRow>
            ) : list.items.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground" data-testid="quotes-empty">
                Aucun devis. Créez un devis depuis la fiche d'un dossier.
              </TableCell></TableRow>
            ) : list.items.map((d) => (
              <TableRow key={d.id} data-testid={`quote-row-${d.id}`}>
                <TableCell className="text-xs font-semibold text-navy whitespace-nowrap">{d.quote_number}</TableCell>
                <TableCell className="font-medium">{d.contact_name || "—"}</TableCell>
                <TableCell className="text-xs">{d.title}</TableCell>
                <TableCell className="text-xs">{d.current_version ? `V${d.current_version}` : "—"}</TableCell>
                <TableCell className="text-xs font-semibold">{euros(d.totals?.total_ttc_cents)}</TableCell>
                <TableCell className="text-xs">{d.valid_until || "—"}</TableCell>
                <TableCell><span className="text-xs font-semibold bg-navy/5 text-navy rounded-full px-2.5 py-1 whitespace-nowrap">{QUOTE_STATUS_LABELS[d.status] || d.status}</span></TableCell>
                <TableCell className="text-xs text-muted-foreground">{formatDate(d.updated_at)}</TableCell>
                <TableCell><Button data-testid={`quote-open-${d.id}`} onClick={() => navigate(`/admin/devis/${d.id}`)} variant="ghost" size="sm" className="text-navy font-semibold">Ouvrir</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <PaginationBar total={list.total} page={list.page} pageSize={list.pageSize} totalPages={list.total_pages}
        onPage={list.setPage} onSize={list.setPageSize} loading={list.loading} testPrefix="quotes-" />
    </AdminShell>
  );
}
